import { useEffect, useRef, useState } from 'react';
import { api } from '../api/endpoints';
import type { LockBackgroundTv, TvLockBackground } from '../api/types';
import { usePoll } from '../hooks/usePoll';
import { formatAgo } from '../lib/format';
import hhgLogo from '../assets/hhg-logo.png';
import { Badge, Button, Card, ErrorNote } from './ui';
import { useToast } from './Toast';

// The picture behind the lock screen on every PS5 TV (TV agent 0.1.3+). One
// choice for all the TVs; they switch within seconds, with no install. It is
// cosmetic: nothing here can unlock a seat or change time, and a TV that
// cannot load the picture keeps its built-in background.

// The TVs draw at 1920×1080; anything bigger is shrunk here so a phone photo
// uploads quickly and stays well under the server's 2.5 MB limit.
const MAX_W = 1920;
const MAX_H = 1080;

// The same dark layer the TV lays over a picture (PICTURE_DIM in the TV
// agent's AuroraView), so the preview shows what customers will actually see
// and staff can judge the text is readable. Keep the two in step.
const TV_DIM = 'rgba(7, 11, 20, 0.62)';

// A still of the TVs' built-in background: drifting cyan, blue and orange
// light over deep navy under a faint dot grid.
const AURORA_STYLE: React.CSSProperties = {
  backgroundColor: '#070b14',
  backgroundImage: [
    'radial-gradient(circle at 1px 1px, rgba(148,163,184,0.12) 1px, transparent 0)',
    'radial-gradient(ellipse at 22% 30%, rgba(34,211,238,0.45), transparent 55%)',
    'radial-gradient(ellipse at 80% 72%, rgba(249,115,22,0.40), transparent 50%)',
    'radial-gradient(ellipse at 62% 18%, rgba(59,130,246,0.35), transparent 45%)',
    'radial-gradient(ellipse at 35% 85%, rgba(245,158,11,0.22), transparent 40%)',
  ].join(', '),
  backgroundSize: '12px 12px, 100% 100%, 100% 100%, 100% 100%, 100% 100%',
};

/** Object URLs for stored pictures, fetched once each. A picture's name is its
 *  content, so a URL never goes stale while the card is open. */
function useImageUrls(shas: string[]): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const made = useRef<Record<string, string>>({});
  const key = shas.join(',');

  useEffect(() => {
    const controller = new AbortController();
    for (const sha of shas) {
      if (made.current[sha]) continue;
      made.current[sha] = 'pending';
      api.tvLockBackground
        .image(sha, controller.signal)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          made.current[sha] = url;
          setUrls((prev) => ({ ...prev, [sha]: url }));
        })
        .catch(() => {
          // Shown as a blank tile; the next poll tries again.
          delete made.current[sha];
        });
    }
    return () => controller.abort();
  }, [key]);

  useEffect(
    () => () => {
      for (const url of Object.values(made.current)) {
        if (url !== 'pending') URL.revokeObjectURL(url);
      }
    },
    [],
  );

  return urls;
}

/** Shrinks a picture to fit 1920×1080 and re-encodes it as JPEG. */
async function prepareImage(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_W / bitmap.width, MAX_H / bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot prepare the picture.');
  // A transparent PNG would otherwise turn black where it is see-through.
  ctx.fillStyle = '#070b14';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not prepare the picture.'))),
      'image/jpeg',
      0.85,
    ),
  );
}

/** A 16:9 mock of the TV's lock screen, scaled to whatever width it gets. */
function LockPreview({ imageUrl, stationName }: { imageUrl: string | null; stationName: string }) {
  return (
    <div
      className="relative aspect-video w-full overflow-hidden rounded-lg border border-slate-700 [container-type:inline-size]"
      style={imageUrl ? undefined : AURORA_STYLE}
    >
      {imageUrl && (
        <>
          <img src={imageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ backgroundColor: TV_DIM }} />
        </>
      )}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <img src={hhgLogo} alt="" className="w-[11cqw]" />
        <p className="mt-[0.6cqw] text-[2.7cqw] font-bold tracking-wide text-slate-50">
          {stationName}
        </p>
        <div className="mt-[0.7cqw] h-[0.2cqw] w-[7.3cqw] rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-orange-500" />
        <p className="mt-[1.1cqw] text-[1.35cqw] font-light text-slate-400">
          Ask at the counter to start playing
        </p>
      </div>
    </div>
  );
}

function TvStatus({ tv, active }: { tv: LockBackgroundTv; active: string | null }) {
  let badge: React.ReactNode;
  if (!tv.paired) badge = <Badge>Not paired</Badge>;
  else if (tv.showing === null) badge = <Badge tone="amber">Update TV agent to 0.1.3</Badge>;
  else if (!tv.online) badge = <Badge>Offline</Badge>;
  else if (tv.showing.sha256 === active) badge = <Badge tone="green">✓ Showing it</Badge>;
  else badge = <Badge tone="sky">Updating…</Badge>;
  return (
    <span className="inline-flex items-center gap-2 text-sm text-slate-300">
      {tv.name} {badge}
    </span>
  );
}

export function TvLockScreenCard() {
  const toast = useToast();
  const { data, error, loading, refresh } = usePoll<TvLockBackground>(
    (signal) => api.tvLockBackground.get(signal),
    5000,
  );
  const urls = useImageUrls(data?.images.map((i) => i.sha256) ?? []);

  const fileInput = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<{ blob: Blob; url: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => () => {
    if (pending) URL.revokeObjectURL(pending.url);
  }, [pending]);

  const active = data?.active_sha256 ?? null;
  const previewName = data?.tvs[0]?.name ?? 'PS5-01';

  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Choose a JPEG or PNG picture.');
      return;
    }
    try {
      const blob = await prepareImage(file);
      setPending({ blob, url: URL.createObjectURL(blob) });
    } catch (err) {
      toast.error((err as Error).message || 'Could not read that picture.');
    }
  };

  const send = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await api.tvLockBackground.upload(pending.blob);
      toast.success('New lock screen sent to the TVs.');
      setPending(null);
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const use = async (sha256: string | null) => {
    if (sha256 === active) return;
    setBusy(true);
    try {
      await api.tvLockBackground.use(sha256);
      toast.success(
        sha256 === null
          ? 'The TVs are going back to the built-in lock screen.'
          : 'Lock screen sent to the TVs.',
      );
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const previewUrl = pending ? pending.url : active ? (urls[active] ?? null) : null;

  return (
    <Card>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-semibold text-slate-100">TV lock screen</h2>
        {data?.updated_at && data.updated_by_name && (
          <span className="text-xs text-slate-500">
            Changed {formatAgo(data.updated_at)} by {data.updated_by_name}
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-400">
        The picture behind the lock screen on every PS5 TV. It never affects
        locking or time.
      </p>

      {error && !data && (
        <div className="mt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      )}

      {loading && !data ? null : (
        <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <div>
            <LockPreview imageUrl={previewUrl} stationName={previewName} />
            {pending && (
              <p className="mt-2 text-xs text-amber-300">
                Preview only. Nothing changes on the TVs until you press Use on all TVs.
              </p>
            )}
          </div>

          <div className="space-y-4">
            {pending ? (
              <div className="flex flex-wrap gap-2">
                <Button onClick={send} disabled={busy}>
                  {busy ? 'Sending…' : 'Use on all TVs'}
                </Button>
                <Button variant="secondary" onClick={() => setPending(null)} disabled={busy}>
                  Cancel
                </Button>
              </div>
            ) : (
              <div>
                <Button
                  variant="secondary"
                  onClick={() => fileInput.current?.click()}
                  disabled={busy}
                >
                  Upload picture…
                </Button>
                <p className="mt-1.5 text-xs text-slate-500">
                  JPEG or PNG. Wide pictures work best (16:9). Large photos are
                  shrunk to 1920×1080 before sending.
                </p>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={(event) => {
                    void pick(event.target.files?.[0]);
                    event.target.value = '';
                  }}
                />
              </div>
            )}

            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                Choose
              </p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => void use(null)}
                  disabled={busy || !!pending}
                  aria-pressed={active === null}
                  className={`relative aspect-video overflow-hidden rounded-md border-2 disabled:cursor-not-allowed ${active === null ? 'border-sky-400' : 'border-slate-700 hover:border-slate-500'}`}
                  style={AURORA_STYLE}
                >
                  <span className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5 text-[11px] text-slate-200">
                    Built-in
                  </span>
                </button>
                {data?.images.map((image) => (
                  <button
                    key={image.sha256}
                    type="button"
                    onClick={() => void use(image.sha256)}
                    disabled={busy || !!pending}
                    aria-pressed={active === image.sha256}
                    aria-label={`Picture uploaded ${formatAgo(image.uploaded_at)}`}
                    className={`relative aspect-video overflow-hidden rounded-md border-2 bg-slate-800 disabled:cursor-not-allowed ${active === image.sha256 ? 'border-sky-400' : 'border-slate-700 hover:border-slate-500'}`}
                  >
                    {urls[image.sha256] && (
                      <img
                        src={urls[image.sha256]}
                        alt=""
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {data && data.tvs.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                  On the TVs now
                </p>
                <div className="flex flex-col gap-1.5">
                  {data.tvs.map((tv) => (
                    <TvStatus key={tv.station_id} tv={tv} active={active} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
